import { type PointerEvent as ReactPointerEvent, type ReactElement, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { circleToRing, radiusThroughPoint } from '@vello/geofence';
import { Button, MapBasemap, MapRouteMarker, MapStage, useColorScheme, MapTile, MapOverlay, MapMarker, MapControls, MapAttribution } from '@vello/web-theme';
import { BASEMAP_ZOOM, basemapStyleUrl, clampZoom, fitView, fromScreen, tileUrl, toScreen, visibleTiles, type TileSource } from '@vello/maps-ui';
import { type MapPoint } from '../../src/index';
import { useT } from '../i18n/provider';
import { useAdmin } from '../session';

/**
 * The Admin's slippy map (W9-OPS-LIVE-MAP, D471, ADR-0089): the platform's own vector basemap (D797, `MapBasemap` over the
 * api's `/v1/geo/tiles/style.json`, the same map the native apps draw) or, when Integrações › Tiles do mapa holds a raster
 * source (an override, never a provider in code), those tiles; the markers as real buttons (48 px hit area, the dot inside
 * is smaller), optional route lines, zoom buttons, a "fit" button and pointer panning. Every colour is a token variable
 * (map pins may be semantic, IMPORT-0001 §4).
 */
export interface MapLine {
  readonly id: string;
  readonly points: readonly LatLng[];
  readonly colorVar: string;
  readonly dashed?: boolean;
  readonly label: string;
}

/** The map's own coordinate shape (structurally `@vello/geo` `LatLng`). */
type LatLng = { readonly latitude: number; readonly longitude: number };

/**
 * An area drawn on the map (W9-RISK-AREAS-ALERTS, D474): a closed outline with a token colour. `draft` is the shape the
 * operator is drawing — dashed, with a dot on each vertex.
 */
export interface MapArea {
  readonly id: string;
  readonly points: readonly LatLng[];
  readonly colorVar: string;
  readonly label: string;
}

/**
 * D616: a ring being drawn in a multi-ring editor. Dashed, with a dot on each vertex; the `active` ring (the one the next tap
 * lands on) is drawn in the primary text token, the others in the secondary one. `closed` joins the last vertex to the first.
 */
export interface MapDraftRing {
  readonly id: string;
  readonly points: readonly LatLng[];
  readonly active: boolean;
  /** The ring's number in the editor; with it the ACTIVE ring offers vertex handles (drag) and edge-midpoint handles (insert). */
  readonly ring?: number;
}

/**
 * D616b: a service area defined by a centre and a radius. Drawn as the dashed active outline (no vertex dots) with a dot on the
 * centre and ONE handle on the east edge; dragging the handle redraws the circle live and calls `onCommit` once, on release, with
 * the radius it stands for. Without `onCommit` (a reader) the circle is drawn and there is no handle.
 */
export interface MapRadiusEdit {
  readonly center: LatLng;
  /** The radius to draw (metres); absent while it is missing or unreadable - then only the centre is drawn. */
  readonly radiusMeters: number | undefined;
  readonly handleLabel: string;
  readonly centerLabel: string;
  readonly onCommit?: (radiusMeters: number) => void;
}

/** The circle outline's vertex that lies due east of the centre (bearing 90 degrees = 16 of 64) - where the handle sits. */
const EAST_VERTEX = 16;
const RADIUS_SEGMENTS = 64;

/** A handle's invisible hit circle: a 48 px target (radius 24) around the visible dot. */
const HANDLE_HIT_RADIUS = 24;

/** What a handle drag is doing: moving vertex `index` of `ring`, or inserting a new vertex at `index` (from an edge midpoint). */
interface HandleDrag {
  readonly kind: 'move' | 'insert' | 'radius';
  readonly ring: number;
  readonly index: number;
  readonly x: number;
  readonly y: number;
  readonly start: LatLng;
  moved: boolean;
  point: LatLng;
}

const midpointOf = (a: LatLng, b: LatLng): LatLng => ({ latitude: (a.latitude + b.latitude) / 2, longitude: (a.longitude + b.longitude) / 2 });


/** A pointer that moves less than this many pixels between down and up is a tap (adds a vertex), not a pan. */
const TAP_SLOP_PX = 6;

/** A pin closer than this to the stage's top edge opens its bubble below; closer than TIP_EDGE_PX to a side edge anchors it to that side. */
const TIP_ROOM_PX = 150;
const TIP_EDGE_PX = 150;

const HEIGHT = 440;
const DEFAULT_WIDTH = 720;

export function MapCanvas({
  tiles,
  center,
  points,
  lines = [],
  areas = [],
  draft,
  draftRings = [],
  focus,
  onMapTap,
  onVertexMove,
  onEdgeInsert,
  radius,
  routeEnds,
  selectedId,
  onSelect,
  ariaLabel,
  pointLabel,
  pointTip,
}: {
  /** The raster override of Integrações › Tiles do mapa; absent → the platform basemap (D797), zoom 3–18. */
  readonly tiles?: TileSource | undefined;
  readonly center: LatLng | undefined;
  readonly points: readonly MapPoint[];
  readonly lines?: readonly MapLine[];
  /** D474: the market's risk areas (closed outlines, token colours). */
  readonly areas?: readonly MapArea[];
  /** D474: the outline being drawn (its vertices, in order). */
  readonly draft?: readonly LatLng[];
  /** D616: the rings of a service-area drawing (several, one active). */
  readonly draftRings?: readonly MapDraftRing[];
  /** D616: a set of points to fit the view to whenever `key` changes (a drawing loaded as a whole). */
  readonly focus?: { readonly key: string; readonly points: readonly LatLng[] };
  /** D474: a tap on the map (not a pan) — the point under it. */
  readonly onMapTap?: (point: LatLng) => void;
  /** D616: opt-in vertex editing — a vertex of the active draft ring was dragged; called ONCE, on release, with the final point. */
  readonly onVertexMove?: (ring: number, index: number, point: LatLng) => void;
  /** D616: opt-in — an edge-midpoint handle of the active draft ring was tapped (the midpoint) or dragged (where it was released): insert a vertex at `atIndex`. */
  readonly onEdgeInsert?: (ring: number, atIndex: number, point: LatLng) => void;
  /** D616b: the service area is a centre and a radius (drawn as a circle with one draggable handle). */
  readonly radius?: MapRadiusEdit;
  /** D800: a ride's ends, drawn as the Design System's markers (embarque, destino) over the lines. */
  readonly routeEnds?: { readonly pickup?: LatLng; readonly dropoff?: LatLng };
  readonly selectedId?: string | undefined;
  readonly onSelect?: (point: MapPoint) => void;
  readonly ariaLabel: string;
  readonly pointLabel: (point: MapPoint) => string;
  /** The details bubble of a pin (hover and keyboard focus); phrasing content, one `MapMarkerTipLine` per line. */
  readonly pointTip?: (point: MapPoint) => ReactNode;
}): ReactElement {
  const { t } = useT();
  const { runtime } = useAdmin();
  // D798: the navy basemap when the console is drawn dark.
  const scheme = useColorScheme();
  const basemap = tiles === undefined ? basemapStyleUrl(runtime.apiBaseUrl, scheme) : undefined;
  const [credit, setCredit] = useState<string | undefined>(undefined);
  const zoomBounds = tiles ?? BASEMAP_ZOOM;
  const host = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(DEFAULT_WIDTH);
  const viewport = useMemo(() => ({ width, height: HEIGHT }), [width]);
  const [view, setView] = useState<{ readonly center: LatLng; readonly zoom: number } | undefined>(undefined);
  const handleDrag = useRef<HandleDrag | undefined>(undefined);
  const [live, setLive] = useState<{ readonly kind: 'move' | 'insert' | 'radius'; readonly ring: number; readonly index: number; readonly point: LatLng } | undefined>(undefined);
  const drag = useRef<{ readonly x: number; readonly y: number; readonly center: LatLng; moved: boolean } | undefined>(undefined);

  useEffect(() => {
    const el = host.current;
    if (el === null || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      const w = Math.round(el.getBoundingClientRect().width);
      if (w > 0) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fitTargets = useMemo<readonly LatLng[]>(() => {
    const all: LatLng[] = points.map((p) => ({ latitude: p.latitude, longitude: p.longitude }));
    for (const l of lines) all.push(...l.points);
    for (const a of areas) all.push(...a.points);
    if (focus !== undefined) all.push(...focus.points);
    return all;
  }, [points, lines, areas, focus]);
  // The drawn rings are NOT fit targets: the map must not jump at every tap. A screen that loads a whole drawing (an import,
  // "start from the published area") hands it over as `focus`, whose key says when to fit again.
  const fitKey = fitTargets.length === 0 ? 'none' : `${fitTargets.length}:${fitTargets[0]?.latitude}:${fitTargets[0]?.longitude}:${focus?.key ?? ''}`;

  // Fit once per data set (and when the width settles); a point refresh does not undo the operator's own pan or zoom.
  const fittedFor = useRef<string | undefined>(undefined);
  useEffect(() => {
    const key = `${fitKey}@${width}`;
    if (fittedFor.current === key) return;
    fittedFor.current = key;
    const fit = fitView(fitTargets, viewport, zoomBounds);
    if (fit !== undefined) setView(fit);
    else if (center !== undefined) setView({ center, zoom: clampZoom(12, zoomBounds) });
  }, [fitKey, fitTargets, viewport, zoomBounds, center, width]);

  const current = view ?? (center !== undefined ? { center, zoom: clampZoom(12, zoomBounds) } : { center: { latitude: 0, longitude: 0 }, zoom: zoomBounds.minZoom });
  const shown = tiles !== undefined ? visibleTiles(current, viewport) : [];
  const attribution = tiles !== undefined ? tiles.attribution : credit;

  // While a handle is dragged the ring is drawn with the vertex where the pointer is (the edit is committed on release).
  const shownRings = useMemo<readonly MapDraftRing[]>(() => {
    if (live === undefined || live.kind === 'radius') return draftRings;
    return draftRings.map((r) => {
      if (r.ring !== live.ring) return r;
      const pts = [...r.points];
      if (live.kind === 'move') pts[live.index] = live.point;
      else pts.splice(live.index, 0, live.point);
      return { ...r, points: pts };
    });
  }, [draftRings, live]);

  // The radius circle: the committed radius, or the one under the pointer while the handle is dragged.
  const radiusCenter = radius?.center;
  const liveRadius = live?.kind === 'radius' && radiusCenter !== undefined ? radiusThroughPoint(radiusCenter, live.point) : undefined;
  const radiusShown = liveRadius !== undefined && Number.isFinite(liveRadius) ? liveRadius : radius?.radiusMeters;
  const radiusLat = radiusCenter?.latitude;
  const radiusLng = radiusCenter?.longitude;
  const radiusRing = useMemo<readonly LatLng[]>(
    () => (radiusLat !== undefined && radiusLng !== undefined && radiusShown !== undefined ? circleToRing({ latitude: radiusLat, longitude: radiusLng }, radiusShown, RADIUS_SEGMENTS) : []),
    [radiusLat, radiusLng, radiusShown],
  );

  const radiusCenterScreen = radiusCenter !== undefined ? toScreen(radiusCenter, current, viewport) : undefined;
  const radiusHandle = radiusRing.length > EAST_VERTEX ? (radiusRing[EAST_VERTEX] as LatLng) : undefined;
  const radiusHandleScreen = radiusHandle !== undefined ? toScreen(radiusHandle, current, viewport) : undefined;

  const zoomBy = (delta: number): void => setView({ center: current.center, zoom: clampZoom(current.zoom + delta, zoomBounds) });
  const refit = (): void => {
    const fit = fitView([...fitTargets, ...radiusRing], viewport, zoomBounds);
    if (fit !== undefined) setView(fit);
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>): void => {
    if ((e.target as HTMLElement).closest('button') !== null) return;
    drag.current = { x: e.clientX, y: e.clientY, center: current.center, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const pointAt = (e: ReactPointerEvent<HTMLElement>): LatLng => {
    const rect = e.currentTarget.getBoundingClientRect();
    return fromScreen({ left: e.clientX - rect.left, top: e.clientY - rect.top }, current, viewport);
  };
  const startHandle = (e: ReactPointerEvent<SVGElement>, kind: 'move' | 'insert' | 'radius', ring: number, index: number, start: LatLng): void => {
    // The handle owns this gesture: the stage must neither pan nor take it for a tap that adds a vertex.
    e.stopPropagation();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    handleDrag.current = { kind, ring, index, x: e.clientX, y: e.clientY, start, moved: false, point: start };
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>): void => {
    const h = handleDrag.current;
    if (h !== undefined) {
      if (Math.hypot(e.clientX - h.x, e.clientY - h.y) > TAP_SLOP_PX) h.moved = true;
      if (h.moved) {
        h.point = pointAt(e);
        setLive({ kind: h.kind, ring: h.ring, index: h.index, point: h.point });
      }
      return;
    }
    const d = drag.current;
    if (d === undefined) return;
    if (Math.hypot(e.clientX - d.x, e.clientY - d.y) > TAP_SLOP_PX) d.moved = true;
    if (!d.moved) return;
    const next = fromScreen({ left: viewport.width / 2 - (e.clientX - d.x), top: viewport.height / 2 - (e.clientY - d.y) }, { center: d.center, zoom: current.zoom }, viewport);
    setView({ center: next, zoom: current.zoom });
  };
  const onPointerUp = (e?: ReactPointerEvent<HTMLDivElement>): void => {
    const h = handleDrag.current;
    if (h !== undefined) {
      handleDrag.current = undefined;
      setLive(undefined);
      if (e === undefined || e.type === 'pointercancel') return;
      // ONE edit per gesture: a moved vertex, or an inserted one (a tap on the midpoint inserts exactly at the midpoint).
      if (h.kind === 'radius') {
        if (h.moved && radius?.onCommit !== undefined) {
          const meters = radiusThroughPoint(radius.center, h.point);
          if (Number.isFinite(meters) && meters > 0) radius.onCommit(meters);
        }
      } else if (h.kind === 'move') {
        if (h.moved) onVertexMove?.(h.ring, h.index, h.point);
      } else onEdgeInsert?.(h.ring, h.index, h.moved ? h.point : h.start);
      return;
    }
    const d = drag.current;
    drag.current = undefined;
    if (e === undefined || d === undefined || d.moved || onMapTap === undefined || e.type === 'pointercancel') return;
    const rect = e.currentTarget.getBoundingClientRect();
    onMapTap(fromScreen({ left: e.clientX - rect.left, top: e.clientY - rect.top }, current, viewport));
  };

  return (
    <div>
      <MapStage
        height={HEIGHT}
        ref={host}
        role="group"
        aria-label={ariaLabel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        {basemap !== undefined && <MapBasemap styleUrl={basemap} center={current.center} zoom={current.zoom} onAttribution={setCredit} />}
        {tiles !== undefined && shown.map((tile) => (
          <MapTile key={tile.key} src={tileUrl(tiles.urlTemplate, tile.z, tile.x, tile.y)} left={tile.left} top={tile.top} />
        ))}
        {(areas.length > 0 || draftRings.length > 0 || (draft !== undefined && draft.length > 0)) && (
          <MapOverlay width={viewport.width} height={viewport.height}>
            {areas.map((a) => (
              <polygon
                key={a.id}
                fill={a.colorVar}
                fillOpacity={0.18}
                stroke={a.colorVar}
                strokeWidth={2}
                points={a.points.map((p) => { const s = toScreen(p, current, viewport); return `${s.left.toFixed(1)},${s.top.toFixed(1)}`; }).join(' ')}
              />
            ))}
            {shownRings.map((r) => {
              const screen = r.points.map((p) => toScreen(p, current, viewport));
              const stroke = r.active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)';
              const path = (screen.length >= 3 ? [...screen, screen[0] as (typeof screen)[number]] : screen).map((s) => `${s.left.toFixed(1)},${s.top.toFixed(1)}`).join(' ');
              return (
                <g key={r.id}>
                  <polyline fill="none" stroke={stroke} strokeWidth={r.active ? 3 : 2} strokeDasharray="8 6" points={path} />
                  {screen.map((s, i) => (
                    <circle key={`${r.id}:${i}`} cx={s.left} cy={s.top} r={r.active ? 5 : 4} fill="var(--color-surface)" stroke={stroke} strokeWidth={2} />
                  ))}
                </g>
              );
            })}
            {draft !== undefined && draft.length > 0 && (
              <>
                <polyline
                  fill="none"
                  stroke="var(--color-text-primary)"
                  strokeWidth={2}
                  strokeDasharray="8 6"
                  points={(draft.length >= 3 ? [...draft, draft[0] as LatLng] : draft).map((p) => { const s = toScreen(p, current, viewport); return `${s.left.toFixed(1)},${s.top.toFixed(1)}`; }).join(' ')}
                />
                {draft.map((p, i) => {
                  const s = toScreen(p, current, viewport);
                  return <circle key={`${i}:${p.latitude}:${p.longitude}`} cx={s.left} cy={s.top} r={5} fill="var(--color-surface)" stroke="var(--color-text-primary)" strokeWidth={2} />;
                })}
              </>
            )}
          </MapOverlay>
        )}
        {radius !== undefined && (
          <MapOverlay width={viewport.width} height={viewport.height}>
            {radiusRing.length >= 3 && (
              <polyline
                data-radius-outline=""
                fill="none"
                stroke="var(--color-text-primary)"
                strokeWidth={3}
                strokeDasharray="8 6"
                points={[...radiusRing, radiusRing[0] as LatLng].map((p) => { const s = toScreen(p, current, viewport); return `${s.left.toFixed(1)},${s.top.toFixed(1)}`; }).join(' ')}
              />
            )}
            {radiusCenterScreen !== undefined && (
              <circle data-radius-center="" cx={radiusCenterScreen.left} cy={radiusCenterScreen.top} r={6} fill="var(--color-text-primary)" stroke="var(--color-surface)" strokeWidth={2}>
                <title>{radius.centerLabel}</title>
              </circle>
            )}
            {radius.onCommit !== undefined && radiusHandle !== undefined && radiusHandleScreen !== undefined && (
              // The handle stays mounted while it is dragged: the element that holds the pointer must outlive the gesture.
              <g data-handle="radius" pointerEvents="all" onPointerDown={(e) => startHandle(e, 'radius', -1, 0, radiusHandle)}>
                <circle cx={radiusHandleScreen.left} cy={radiusHandleScreen.top} r={HANDLE_HIT_RADIUS} fill="transparent" />
                <circle cx={radiusHandleScreen.left} cy={radiusHandleScreen.top} r={8} fill="var(--color-surface)" stroke="var(--color-text-primary)" strokeWidth={3} />
                <title>{radius.handleLabel}</title>
              </g>
            )}
          </MapOverlay>
        )}
        {(onVertexMove !== undefined || onEdgeInsert !== undefined) && (
          <MapOverlay width={viewport.width} height={viewport.height}>
            {shownRings
              .filter((r) => r.active && r.ring !== undefined)
              .map((r) => {
                const ring = r.ring as number;
                const screen = r.points.map((p) => toScreen(p, current, viewport));
                // The handles stay mounted while one is dragged: the element that holds the pointer must outlive the gesture.
                const edges = r.points.length >= 3 ? r.points.length : r.points.length === 2 ? 1 : 0;
                return (
                  <g key={`handles:${r.id}`}>
                    {onEdgeInsert !== undefined &&
                      Array.from({ length: edges }, (_, i) => {
                        const a = r.points[i] as LatLng;
                        const b = r.points[(i + 1) % r.points.length] as LatLng;
                        const mid = midpointOf(a, b);
                        const m = toScreen(mid, current, viewport);
                        return (
                          <g key={`mid:${i}`} data-handle={`edge:${ring}:${i}`} pointerEvents="all" onPointerDown={(e) => startHandle(e, 'insert', ring, i + 1, mid)}>
                            <circle cx={m.left} cy={m.top} r={HANDLE_HIT_RADIUS} fill="transparent" />
                            <circle cx={m.left} cy={m.top} r={3} fill="var(--color-surface)" stroke="var(--color-text-secondary)" strokeWidth={1.5} />
                          </g>
                        );
                      })}
                    {onVertexMove !== undefined &&
                      screen.map((s, i) => (
                        <g key={`vtx:${i}`} data-handle={`vertex:${ring}:${i}`} pointerEvents="all" onPointerDown={(e) => startHandle(e, 'move', ring, i, r.points[i] as LatLng)}>
                          <circle cx={s.left} cy={s.top} r={HANDLE_HIT_RADIUS} fill="transparent" />
                        </g>
                      ))}
                  </g>
                );
              })}
          </MapOverlay>
        )}
        {lines.length > 0 && (
          <MapOverlay width={viewport.width} height={viewport.height}>
            {lines.map((l) => (
              <polyline
                key={l.id}
                fill="none"
                stroke={l.colorVar}
                strokeWidth={4}
                strokeLinecap="round"
                strokeLinejoin="round"
                {...(l.dashed === true ? { strokeDasharray: '10 8' } : {})}
                points={l.points
                  .map((p) => {
                    const s = toScreen(p, current, viewport);
                    return `${s.left.toFixed(1)},${s.top.toFixed(1)}`;
                  })
                  .join(' ')}
              />
            ))}
          </MapOverlay>
        )}
        {routeEnds?.pickup !== undefined && (() => { const s = toScreen(routeEnds.pickup, current, viewport); return <MapRouteMarker kind="pickup" left={s.left} top={s.top} />; })()}
        {routeEnds?.dropoff !== undefined && (() => { const s = toScreen(routeEnds.dropoff, current, viewport); return <MapRouteMarker kind="dropoff" left={s.left} top={s.top} />; })()}
        {points.map((p) => {
          const s = toScreen({ latitude: p.latitude, longitude: p.longitude }, current, viewport);
          if (s.left < -24 || s.top < -24 || s.left > viewport.width + 24 || s.top > viewport.height + 24) return null;
          const selected = p.id === selectedId;
          return (
            <MapMarker
              key={`${p.kind}:${p.id}`}
              left={s.left}
              top={s.top}
              color={p.colorVar}
              shape={p.kind === 'ride' ? 'square' : 'round'}
              stale={p.stale === true}
              selected={selected}
              interactive={onSelect !== undefined}
              label={pointLabel(p)}
              onClick={() => onSelect?.(p)}
              {...(pointTip !== undefined ? { tip: pointTip(p), tipPlacement: s.top < TIP_ROOM_PX ? ('bottom' as const) : ('top' as const), tipAlign: s.left < TIP_EDGE_PX ? ('start' as const) : s.left > viewport.width - TIP_EDGE_PX ? ('end' as const) : ('center' as const) } : {})}
            />
          );
        })}
        <MapControls>
          <Button variant="secondary" aria-label={t('lmap.map.zoomIn')} className="vello-admin-a-tap" onClick={() => zoomBy(1)}>+</Button>
          <Button variant="secondary" aria-label={t('lmap.map.zoomOut')} className="vello-admin-a-tap" onClick={() => zoomBy(-1)}>−</Button>
          <Button variant="secondary" icon="mapPin" aria-label={t('lmap.map.fit')} className="vello-admin-a-tap" onClick={refit} />
        </MapControls>
        {attribution !== undefined && <MapAttribution>{attribution}</MapAttribution>}
      </MapStage>
    </div>
  );
}
